import { useId, useMemo, useState, type MouseEvent } from "react";
import { PageHeader } from "@/components/PageHeader";
import { REPORTS, type ReportRegistryEntry } from "@/lib/reportRegistry";
import "./ReportsPicker.css";

interface ReportsPickerProps {
  onNavigate: (path: string) => void;
}

const FAVOURITES_KEY = "ef.reports.favourites";
const RECENTS_KEY = "ef.reports.recents";
/** How many recents the picker remembers — a shortcut list, not a full history. */
const RECENTS_LIMIT = 5;

/**
 * Reads a JSON array of slugs from localStorage. Defensive because localStorage is
 * outside this app's control: a corrupted value, a quota error, or a prior release's
 * differently-shaped payload must not crash the picker — it just starts from empty.
 */
function readSlugs(key: string): string[] {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((s): s is string => typeof s === "string") : [];
  } catch {
    return [];
  }
}

function writeSlugs(key: string, slugs: string[]): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(slugs));
  } catch {
    // Private browsing / a full quota leaves this visit's in-memory state as the source
    // of truth — the picker still works, it just won't remember next time.
  }
}

/** `"Financial statements"` -> `"financial-statements"`, for a stable heading id. */
function slugifyCategory(category: string): string {
  return category.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

/**
 * ADR 0009 §6 — `/reports` is a searchable picker, not a tab strip: every report the app
 * knows about (`reportRegistry.ts`), grouped by category, with localStorage-backed
 * Favourites and Recents. Replaces the retired `AreaTabs` strip (`REPORTS_TABS`).
 *
 * Favourites and Recents are deliberately NOT filtered by the search box — they are
 * already curated, short lists (a star the farmer chose, the last few they opened), not
 * a second copy of the full catalogue to search through.
 */
export function ReportsPicker({ onNavigate }: ReportsPickerProps) {
  const [query, setQuery] = useState("");
  const [favourites, setFavourites] = useState<string[]>(() => readSlugs(FAVOURITES_KEY));
  const [recents, setRecents] = useState<string[]>(() => readSlugs(RECENTS_KEY));
  const searchId = useId();

  const byslug = useMemo(() => new Map(REPORTS.map((r) => [r.slug, r] as const)), []);

  function toggleFavourite(slug: string): void {
    setFavourites((current) => {
      const next = current.includes(slug)
        ? current.filter((s) => s !== slug)
        : [...current, slug];
      writeSlugs(FAVOURITES_KEY, next);
      return next;
    });
  }

  function recordRecent(slug: string): void {
    setRecents((current) => {
      const next = [slug, ...current.filter((s) => s !== slug)].slice(0, RECENTS_LIMIT);
      writeSlugs(RECENTS_KEY, next);
      return next;
    });
  }

  /**
   * Real `<a>` elements stay real links — a modified click (new tab, new window,
   * middle-click) or a right-click keeps working like any other link on the web. Only a
   * plain left click is intercepted for client-side navigation, same convention
   * `AppShell.tsx`'s own `handleNavClick` already uses for the primary nav.
   */
  function handleOpen(event: MouseEvent<HTMLAnchorElement>, entry: ReportRegistryEntry): void {
    if (
      event.defaultPrevented
      || event.button !== 0
      || event.metaKey
      || event.ctrlKey
      || event.shiftKey
      || event.altKey
    ) {
      return;
    }
    event.preventDefault();
    recordRecent(entry.slug);
    onNavigate(`/reports/${entry.slug}`);
  }

  const normalizedQuery = query.trim().toLowerCase();
  const filtered = normalizedQuery
    ? REPORTS.filter((r) => r.title.toLowerCase().includes(normalizedQuery))
    : REPORTS;

  const categories = useMemo(() => {
    const order: string[] = [];
    const byCategory = new Map<string, ReportRegistryEntry[]>();
    for (const entry of filtered) {
      if (!byCategory.has(entry.category)) {
        byCategory.set(entry.category, []);
        order.push(entry.category);
      }
      byCategory.get(entry.category)!.push(entry);
    }
    return order.map((category) => ({ category, entries: byCategory.get(category)! }));
  }, [filtered]);

  const favouriteEntries = favourites
    .map((slug) => byslug.get(slug))
    .filter((e): e is ReportRegistryEntry => e !== undefined);
  const recentEntries = recents
    .map((slug) => byslug.get(slug))
    .filter((e): e is ReportRegistryEntry => e !== undefined);

  function renderItem(entry: ReportRegistryEntry) {
    const isFavourite = favourites.includes(entry.slug);
    return (
      <li key={entry.slug} className="reports-picker__item">
        <div className="reports-picker__item-main">
          <a
            href={`/reports/${entry.slug}`}
            className="reports-picker__link"
            onClick={(event) => handleOpen(event, entry)}
          >
            {entry.title}
          </a>
          {entry.description && <p className="reports-picker__desc">{entry.description}</p>}
        </div>
        <button
          type="button"
          className="reports-picker__fav"
          aria-pressed={isFavourite}
          aria-label={`${isFavourite ? "Remove" : "Add"} ${entry.title} ${isFavourite ? "from" : "to"} favourites`}
          onClick={() => toggleFavourite(entry.slug)}
        >
          <span aria-hidden="true">{isFavourite ? "★" : "☆"}</span>
        </button>
      </li>
    );
  }

  return (
    <main className="reports-picker">
      <PageHeader title="Reports" subtitle="Every report EasyFarm can show you, in one place." />

      <div className="reports-picker__search">
        <label htmlFor={searchId} className="reports-picker__search-label">
          Search reports
        </label>
        <input
          id={searchId}
          type="search"
          className="reports-picker__search-input"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Try “hours” or “labour”"
          autoComplete="off"
        />
      </div>

      {favouriteEntries.length > 0 && (
        <section className="reports-picker__group" aria-labelledby="reports-picker-favourites">
          <h2 id="reports-picker-favourites" className="reports-picker__group-title">
            Favourites
          </h2>
          <ul className="reports-picker__list">{favouriteEntries.map(renderItem)}</ul>
        </section>
      )}

      {recentEntries.length > 0 && (
        <section className="reports-picker__group" aria-labelledby="reports-picker-recents">
          <h2 id="reports-picker-recents" className="reports-picker__group-title">
            Recents
          </h2>
          <ul className="reports-picker__list">{recentEntries.map(renderItem)}</ul>
        </section>
      )}

      {categories.length === 0 ? (
        <p className="reports-picker__empty">
          No reports match “{query.trim()}”. Try a different word, or clear the search.
        </p>
      ) : (
        categories.map(({ category, entries }) => {
          const headingId = `reports-picker-cat-${slugifyCategory(category)}`;
          return (
            <section key={category} className="reports-picker__group" aria-labelledby={headingId}>
              <h2 id={headingId} className="reports-picker__group-title">
                {category}
              </h2>
              <ul className="reports-picker__list">{entries.map(renderItem)}</ul>
            </section>
          );
        })
      )}
    </main>
  );
}
